/**
 * Dialog richiesta di modifica ordine già inviato (agente → magazzino).
 * Consente: rimozione righe, cambio quantità, aggiunta articoli (da listino o liberi).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Undo2, Search, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { DecimalInput } from "./DecimalInput";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CR_STATUS_BADGE = {
  pending: { label: "⏳ In attesa", cls: "bg-amber-100 text-amber-800" },
  accepted: { label: "✅ Accettata", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "❌ Rifiutata", cls: "bg-red-100 text-red-700" },
};

export const OrderChangeRequestDialog = ({ order, onClose, onSubmitted }) => {
  const [rows, setRows] = useState(() => (order.items || []).map((it, idx) => ({
    idx, description: it.description, code: it.code, quantity: it.quantity,
    unit: it.unit || "Kg", newQty: it.quantity, remove: false,
  })));
  const [additions, setAdditions] = useState([]);
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order.customer_id) return;
    const params = new URLSearchParams({ customer_id: order.customer_id });
    if (order.pricelist_type_id) params.append("pricelist_type_id", order.pricelist_type_id);
    axios.get(`${API}/agent/catalog?${params}`)
      .then((r) => setCatalog(r.data?.products || []))
      .catch(() => {});
  }, [order.customer_id, order.pricelist_type_id]);

  const q = search.trim().toLowerCase();
  const results = q ? catalog.filter((p) => p.code?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)).slice(0, 8) : [];

  const addFromCatalog = (p) => {
    setAdditions((prev) => [...prev, { product_id: p.id, code: p.code, description: p.description, quantity: 1, unit: p.unit || "Kg", unit_price: p.price || 0, vat_rate: p.vat_rate ?? 0.10 }]);
    setSearch("");
  };
  const addFree = () => setAdditions((prev) => [...prev, { product_id: null, code: "LIBERO", description: "", quantity: 1, unit: "Kg", unit_price: 0, vat_rate: 0.10 }]);
  const updateAdd = (i, patch) => setAdditions((prev) => prev.map((a, x) => (x === i ? { ...a, ...patch } : a)));
  const removeAdd = (i) => setAdditions((prev) => prev.filter((_, x) => x !== i));

  const changes = [
    ...rows.filter((r) => r.remove).map((r) => ({ action: "remove", item_index: r.idx, description: r.description })),
    ...rows.filter((r) => !r.remove && r.newQty !== r.quantity && r.newQty > 0).map((r) => ({ action: "update_qty", item_index: r.idx, description: r.description, old_quantity: r.quantity, quantity: r.newQty })),
    ...additions.filter((a) => a.description.trim() && a.quantity > 0).map((a) => ({ action: "add", ...a })),
  ];

  const submit = async () => {
    if (changes.length === 0) { toast.error("Nessuna modifica da richiedere"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/orders/${order.id}/change-requests`, { changes, note });
      toast.success("Richiesta di modifica inviata al magazzino 🔁");
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio richiesta");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-lg p-4 sm:p-6" data-testid="change-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            🔁 Richiedi modifica — <span className="font-mono text-[#8A1C2A]">{order.order_number}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            La richiesta sarà inviata al magazzino: se accettata, l&apos;ordine verrà aggiornato automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-stone-800 mb-1.5">Articoli dell&apos;ordine</p>
            <div className="border border-stone-200 rounded-lg divide-y divide-stone-100">
              {rows.map((r, i) => (
                <div key={r.idx} className={`p-2.5 flex items-center gap-2 text-sm ${r.remove ? "bg-red-50" : ""}`} data-testid={`cr-row-${r.idx}`}>
                  <div className={`flex-1 min-w-0 ${r.remove ? "line-through text-red-600" : ""}`}>
                    <span className="font-mono text-xs text-stone-400 mr-1.5">{r.code}</span>{r.description}
                  </div>
                  {!r.remove && (
                    <div className="flex items-center gap-1 shrink-0">
                      <DecimalInput value={r.newQty} onValue={(n) => setRows((prev) => prev.map((x, xi) => (xi === i ? { ...x, newQty: n } : x)))} className="w-20 h-8 text-xs text-right bg-white border-stone-300" data-testid={`cr-qty-${r.idx}`} />
                      <span className="text-xs text-stone-500 w-6">{r.unit}</span>
                      {r.newQty !== r.quantity && <Badge className="bg-blue-100 text-blue-800 text-[10px]">era {r.quantity}</Badge>}
                    </div>
                  )}
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setRows((prev) => prev.map((x, xi) => (xi === i ? { ...x, remove: !x.remove, newQty: x.quantity } : x)))} title={r.remove ? "Ripristina" : "Richiedi rimozione"} data-testid={`cr-remove-${r.idx}`}>
                    {r.remove ? <Undo2 className="w-4 h-4 text-stone-500" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-stone-800 mb-1.5">➕ Aggiungi articoli</p>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input placeholder="Cerca dal listino del cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 bg-white border-stone-300" data-testid="cr-search-product" />
              {results.length > 0 && (
                <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                  {results.map((p) => (
                    <button key={p.id} onClick={() => addFromCatalog(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 flex justify-between gap-2" data-testid={`cr-add-${p.code}`}>
                      <span className="min-w-0 truncate"><span className="font-mono text-xs text-[#8A1C2A] mr-1.5">{p.code}</span>{p.description}</span>
                      <span className="text-xs text-stone-500 shrink-0">{p.price > 0 ? `€ ${p.price.toFixed(2)}` : "—"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={addFree} className="mt-2 h-8 text-xs border-stone-300" data-testid="cr-add-free">
              <Plus className="w-3.5 h-3.5 mr-1" /> Articolo libero
            </Button>
            {additions.length > 0 && (
              <div className="mt-2 border border-emerald-200 rounded-lg divide-y divide-emerald-100 bg-emerald-50/40">
                {additions.map((a, i) => (
                  <div key={i} className="p-2.5 flex flex-wrap items-center gap-2 text-sm" data-testid={`cr-addition-${i}`}>
                    <Input value={a.description} onChange={(e) => updateAdd(i, { description: e.target.value })} placeholder="Descrizione articolo *" className="flex-1 min-w-[160px] h-8 text-xs bg-white border-stone-300" data-testid={`cr-addition-desc-${i}`} />
                    <DecimalInput value={a.quantity} onValue={(n) => updateAdd(i, { quantity: n })} className="w-16 h-8 text-xs text-right bg-white border-stone-300" data-testid={`cr-addition-qty-${i}`} />
                    <Input value={a.unit} onChange={(e) => updateAdd(i, { unit: e.target.value })} className="w-14 h-8 text-xs bg-white border-stone-300" />
                    <DecimalInput value={a.unit_price} onValue={(n) => updateAdd(i, { unit_price: n })} placeholder="€/UM" className="w-20 h-8 text-xs text-right bg-white border-stone-300" />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeAdd(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Nota per il magazzino (facoltativa)</Label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" data-testid="cr-note" />
          </div>
        </div>

        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={submit} disabled={saving || changes.length === 0} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="cr-submit">
            <Send className="w-4 h-4 mr-1" /> {saving ? "Invio..." : `Invia richiesta (${changes.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Riepilogo leggibile di una singola modifica richiesta. */
export const changeLabel = (ch) => {
  if (ch.action === "remove") return `➖ Rimuovi: ${ch.description}`;
  if (ch.action === "update_qty") return `🔄 ${ch.description}: quantità ${ch.old_quantity ?? "?"} → ${ch.quantity}`;
  return `➕ Aggiungi: ${ch.description} × ${ch.quantity} ${ch.unit || ""}${ch.unit_price ? ` (€ ${Number(ch.unit_price).toFixed(2)})` : ""}`;
};

export default OrderChangeRequestDialog;
